/**
 * Scaffold Routes Tool
 *
 * Generates React Router configuration and NavRoute registry from backend controllers.
 * Scans C# controllers for NavRoute attributes and creates synchronized frontend routes.
 */

import { Tool } from '@modelcontextprotocol/sdk/types.js';
import path from 'path';

import { Config, ScaffoldRoutesInputSchema, ScaffoldRoutesInput, ScaffoldResult, NavRouteEntry } from '../types/index.js';
import { logger } from '../lib/logger.js';
import { findSmartStackStructure } from '../lib/detector.js';
import { ensureDirectory, writeText, readText } from '../utils/fs.js';
import { glob } from 'glob';

export const scaffoldRoutesTool: Tool = {
  name: 'scaffold_routes',
  description: `Generate React Router configuration from backend NavRoute attributes.

Creates:
- navRoutes.generated.ts: Registry of all routes with API paths and permissions
- routes.tsx: React Router configuration with nested routes
- Layout components (optional)

Example:
  scaffold_routes source="controllers" scope="all"

Scans backend controllers for [NavRoute("context.application.module")] attributes
and generates corresponding frontend routing infrastructure.`,
  inputSchema: {
    type: 'object',
    properties: {
      path: {
        type: 'string',
        description: 'Path to SmartStack project root (defaults to configured project path)',
      },
      source: {
        type: 'string',
        enum: ['controllers', 'navigation', 'manual'],
        default: 'controllers',
        description: 'Source for route discovery',
      },
      scope: {
        type: 'string',
        enum: ['all', 'platform', 'business', 'extensions'],
        default: 'all',
        description: 'Scope of routes to generate',
      },
      options: {
        type: 'object',
        properties: {
          outputPath: { type: 'string', description: 'Custom output path' },
          includeLayouts: { type: 'boolean', default: true },
          includeGuards: { type: 'boolean', default: true },
          generateRegistry: { type: 'boolean', default: true },
          dryRun: { type: 'boolean', default: false },
        },
      },
    },
  },
};

export async function handleScaffoldRoutes(
  args: Record<string, unknown>,
  config: Config
): Promise<string> {
  const input = ScaffoldRoutesInputSchema.parse(args);
  logger.info('Scaffolding routes', { source: input.source, scope: input.scope });

  const result = await scaffoldRoutes(input, config);
  return formatResult(result, input);
}

async function scaffoldRoutes(
  input: ScaffoldRoutesInput,
  config: Config
): Promise<ScaffoldResult> {
  const result: ScaffoldResult = {
    success: true,
    files: [],
    instructions: [],
  };

  const { source, scope, options } = input;
  const dryRun = options?.dryRun ?? false;
  const includeLayouts = options?.includeLayouts ?? true;
  const includeGuards = options?.includeGuards ?? true;
  const generateRegistry = options?.generateRegistry ?? true;

  // Use explicit path if provided, otherwise fall back to config
  const projectRoot = input.path || config.smartstack.projectPath;
  const structure = await findSmartStackStructure(projectRoot);
  // Fallback to 'web' folder (not hardcoded to smartstack-web)
  const webPath = structure.web || path.join(projectRoot, 'web');
  const routesPath = options?.outputPath || path.join(webPath, 'src', 'routes');

  // Discover NavRoutes from backend controllers
  const navRoutes = await discoverNavRoutes(structure, scope);

  if (navRoutes.length === 0) {
    result.success = false;
    result.instructions.push('No NavRoute attributes found in controllers');
    return result;
  }

  // Generate NavRoute registry
  if (generateRegistry) {
    const registryContent = generateNavRouteRegistry(navRoutes);
    const registryFile = path.join(routesPath, 'navRoutes.generated.ts');

    if (!dryRun) {
      await ensureDirectory(routesPath);
      await writeText(registryFile, registryContent);
    }
    result.files.push({ path: registryFile, content: registryContent, type: 'created' });
  }

  // Generate React Router configuration
  const routerContent = generateRouterConfig(navRoutes, includeGuards);
  const routerFile = path.join(routesPath, 'index.tsx');

  if (!dryRun) {
    await ensureDirectory(routesPath);
    await writeText(routerFile, routerContent);
  }
  result.files.push({ path: routerFile, content: routerContent, type: 'created' });

  // Generate layouts if requested
  if (includeLayouts) {
    const layoutsPath = path.join(webPath, 'src', 'layouts');
    const contexts = [...new Set(navRoutes.map(r => r.navRoute.split('.')[0]))];

    for (const context of contexts) {
      const layoutContent = generateLayout(context);
      const layoutFile = path.join(layoutsPath, `${capitalize(context)}Layout.tsx`);

      if (!dryRun) {
        await ensureDirectory(layoutsPath);
        await writeText(layoutFile, layoutContent);
      }
      result.files.push({ path: layoutFile, content: layoutContent, type: 'created' });
    }
  }

  // Generate route guards if requested
  if (includeGuards) {
    const guardsContent = generateRouteGuards();
    const guardsFile = path.join(routesPath, 'guards.tsx');

    if (!dryRun) {
      await writeText(guardsFile, guardsContent);
    }
    result.files.push({ path: guardsFile, content: guardsContent, type: 'created' });
  }

  result.instructions.push(`Generated ${navRoutes.length} routes from ${source}`);
  result.instructions.push('Import routes: import { router } from "./routes";');
  result.instructions.push('Use with RouterProvider: <RouterProvider router={router} />');

  return result;
}

async function discoverNavRoutes(
  structure: Awaited<ReturnType<typeof findSmartStackStructure>>,
  scope: string
): Promise<NavRouteEntry[]> {
  const routes: NavRouteEntry[] = [];

  // Determine which API paths to scan based on scope
  const apiPaths: string[] = [];

  if (scope === 'all' || scope === 'platform') {
    // Scan core API (platform/admin controllers)
    if (structure.apiCore) {
      apiPaths.push(structure.apiCore);
    } else if (structure.api && scope === 'platform') {
      apiPaths.push(structure.api);
    }
  }

  if (scope === 'all' || scope === 'extensions' || scope === 'business') {
    // Scan extensions API (business/client-specific controllers)
    if (structure.apiExtensions) {
      apiPaths.push(structure.apiExtensions);
    }
  }

  // Fallback: if no specific paths found, use primary api
  if (apiPaths.length === 0 && structure.api) {
    apiPaths.push(structure.api);
  }

  if (apiPaths.length === 0) {
    logger.warn('No API project found');
    return routes;
  }

  logger.debug('Scanning API paths', { paths: apiPaths, scope });

  // Find all controller files from all API paths
  let controllerFiles: string[] = [];
  for (const apiPath of apiPaths) {
    const files = await glob('**/*Controller.cs', {
      cwd: apiPath,
      absolute: true,
      ignore: ['**/obj/**', '**/bin/**'],
    });
    controllerFiles = controllerFiles.concat(files);
  }

  for (const file of controllerFiles) {
    try {
      const content = await readText(file);
      const navRouteMatch = content.match(/\[NavRoute\s*\(\s*"([^"]+)"(?:\s*,\s*Suffix\s*=\s*"([^"]+)")?\s*\)\]/);

      if (navRouteMatch) {
        const navRoute = navRouteMatch[1];
        const suffix = navRouteMatch[2];

        // Filter by scope
        const context = navRoute.split('.')[0];
        if (scope !== 'all' && context !== scope) {
          continue;
        }

        // Extract controller name
        const controllerMatch = path.basename(file).match(/(.+)Controller\.cs$/);
        const controllerName = controllerMatch ? controllerMatch[1] : 'Unknown';

        // Extract HTTP methods
        const methods: string[] = [];
        if (content.includes('[HttpGet]')) methods.push('GET');
        if (content.includes('[HttpPost]')) methods.push('POST');
        if (content.includes('[HttpPut]')) methods.push('PUT');
        if (content.includes('[HttpDelete]')) methods.push('DELETE');
        if (content.includes('[HttpPatch]')) methods.push('PATCH');

        // Extract permissions (if any)
        const permissions: string[] = [];
        const authorizeMatches = content.matchAll(/\[Authorize\s*\(\s*[^)]*Policy\s*=\s*"([^"]+)"/g);
        for (const match of authorizeMatches) {
          permissions.push(match[1]);
        }

        const fullNavRoute = suffix ? `${navRoute}.${suffix}` : navRoute;

        routes.push({
          navRoute: fullNavRoute,
          apiPath: `/api/${navRoute.replace(/\./g, '/')}${suffix ? `/${suffix}` : ''}`,
          webPath: `/${navRoute.replace(/\./g, '/')}${suffix ? `/${suffix}` : ''}`,
          permissions,
          controller: controllerName,
          methods,
        });
      }
    } catch {
      logger.debug(`Failed to parse controller: ${file}`);
    }
  }

  // Sort by navRoute for consistent output
  return routes.sort((a, b) => a.navRoute.localeCompare(b.navRoute));
}

function generateNavRouteRegistry(routes: NavRouteEntry[]): string {
  const lines: string[] = [
    '/**',
    ' * NavRoute Registry',
    ' *',
    ' * Auto-generated by SmartStack MCP - DO NOT EDIT MANUALLY',
    ' * Run `scaffold_routes` to regenerate',
    ' */',
    '',
    'export interface NavRoute {',
    '  navRoute: string;',
    '  api: string;',
    '  web: string;',
    '  permissions: string[];',
    '  controller?: string;',
    '  methods: string[];',
    '}',
    '',
    'export const ROUTES: Record<string, NavRoute> = {',
  ];

  for (const route of routes) {
    lines.push(`  '${route.navRoute}': {`);
    lines.push(`    navRoute: '${route.navRoute}',`);
    lines.push(`    api: '${route.apiPath}',`);
    lines.push(`    web: '${route.webPath}',`);
    lines.push(`    permissions: [${route.permissions.map(p => `'${p}'`).join(', ')}],`);
    if (route.controller) {
      lines.push(`    controller: '${route.controller}',`);
    }
    lines.push(`    methods: [${route.methods.map(m => `'${m}'`).join(', ')}],`);
    lines.push('  },');
  }

  lines.push('};');
  lines.push('');
  lines.push('/**');
  lines.push(' * Get route configuration by NavRoute path');
  lines.push(' */');
  lines.push('export function getRoute(navRoute: string): NavRoute {');
  lines.push('  const route = ROUTES[navRoute];');
  lines.push('  if (!route) {');
  lines.push('    throw new Error(`Route not found: ${navRoute}`);');
  lines.push('  }');
  lines.push('  return route;');
  lines.push('}');
  lines.push('');
  lines.push('/**');
  lines.push(' * Check if user has permission for route');
  lines.push(' */');
  lines.push('export function hasRoutePermission(navRoute: string, userPermissions: string[]): boolean {');
  lines.push('  const route = ROUTES[navRoute];');
  lines.push('  if (!route || route.permissions.length === 0) return true;');
  lines.push('  return route.permissions.some(p => userPermissions.includes(p));');
  lines.push('}');
  lines.push('');
  lines.push('/**');
  lines.push(' * Get all routes for a context');
  lines.push(' */');
  lines.push('export function getRoutesByContext(context: string): NavRoute[] {');
  lines.push('  return Object.values(ROUTES).filter(r => r.navRoute.startsWith(`${context}.`));');
  lines.push('}');
  lines.push('');
  lines.push('/**');
  lines.push(' * Get web route with optional tenant slug prefix');
  lines.push(' *');
  lines.push(' * @param navRoute - NavRoute path (e.g., \'platform.administration.users\')');
  lines.push(' * @param options - Configuration for URL building');
  lines.push(' * @returns Web route path, optionally prefixed with /t/{slug}');
  lines.push(' *');
  lines.push(' * @example');
  lines.push(' * // Single-tenant mode or no tenant');
  lines.push(' * getWebRoute(\'platform.administration.users\', { isSingleTenant: true })');
  lines.push(' * // Returns: \'/platform/administration/users\'');
  lines.push(' *');
  lines.push(' * @example');
  lines.push(' * // Multi-tenant mode with slug');
  lines.push(' * getWebRoute(\'platform.administration.users\', { slug: \'acme\', isSingleTenant: false })');
  lines.push(' * // Returns: \'/t/acme/platform/administration/users\'');
  lines.push(' */');
  lines.push('export function getWebRoute(');
  lines.push('  navRoute: string,');
  lines.push('  options: {');
  lines.push('    slug?: string | null;');
  lines.push('    isSingleTenant: boolean;');
  lines.push('    isAdminMode?: boolean;');
  lines.push('  } = { isSingleTenant: true }');
  lines.push('): string {');
  lines.push('  const route = ROUTES[navRoute];');
  lines.push('  if (!route) {');
  lines.push('    throw new Error(`Route not found: ${navRoute}`);');
  lines.push('  }');
  lines.push('');
  lines.push('  const webPath = route.web;');
  lines.push('');
  lines.push('  // No prefix needed in single-tenant mode, admin mode, or without slug');
  lines.push('  if (options.isSingleTenant || options.isAdminMode || !options.slug) {');
  lines.push('    return webPath;');
  lines.push('  }');
  lines.push('');
  lines.push('  // Add tenant slug prefix');
  lines.push('  return `/t/${options.slug}${webPath}`;');
  lines.push('}');
  lines.push('');

  return lines.join('\n');
}

function generateRouterConfig(routes: NavRouteEntry[], includeGuards: boolean): string {
  // Group routes by context and application
  const routeTree = buildRouteTree(routes);

  const lines: string[] = [
    '/**',
    ' * React Router Configuration',
    ' *',
    ' * Auto-generated by SmartStack MCP - DO NOT EDIT MANUALLY',
    ' */',
    '',
    "import { createBrowserRouter, RouteObject } from 'react-router-dom';",
    "import { ROUTES } from './navRoutes.generated';",
  ];

  if (includeGuards) {
    lines.push("import { ProtectedRoute, PermissionGuard } from './guards';");
  }

  // Import layouts
  const contexts = Object.keys(routeTree);
  for (const context of contexts) {
    lines.push(`import { ${capitalize(context)}Layout } from '../layouts/${capitalize(context)}Layout';`);
  }

  lines.push('');
  lines.push('// Page imports - customize these paths');

  // Generate page imports (as placeholders)
  for (const route of routes) {
    const pageName = route.navRoute.split('.').map(capitalize).join('');
    lines.push(`// import { ${pageName}Page } from '../pages/${pageName}Page';`);
  }

  lines.push('');
  lines.push('const routes: RouteObject[] = [');

  // Generate route configuration
  for (const [context, applications] of Object.entries(routeTree)) {
    lines.push('  {');
    lines.push(`    path: '${context}',`);
    lines.push(`    element: <${capitalize(context)}Layout />,`);
    lines.push('    children: [');

    for (const [app, modules] of Object.entries(applications as Record<string, NavRouteEntry[]>)) {
      lines.push('      {');
      lines.push(`        path: '${app}',`);
      lines.push('        children: [');

      for (const route of modules) {
        const modulePath = route.navRoute.split('.').slice(2).join('/');
        const pageName = route.navRoute.split('.').map(capitalize).join('');

        if (includeGuards && route.permissions.length > 0) {
          lines.push('          {');
          lines.push(`            path: '${modulePath || ''}',`);
          lines.push(`            element: (`);
          lines.push(`              <PermissionGuard permissions={ROUTES['${route.navRoute}'].permissions}>`);
          lines.push(`                {/* <${pageName}Page /> */}`);
          lines.push(`                <div>TODO: ${pageName}Page</div>`);
          lines.push(`              </PermissionGuard>`);
          lines.push(`            ),`);
          lines.push('          },');
        } else {
          lines.push('          {');
          lines.push(`            path: '${modulePath || ''}',`);
          lines.push(`            element: <div>TODO: ${pageName}Page</div>,`);
          lines.push('          },');
        }
      }

      lines.push('        ],');
      lines.push('      },');
    }

    lines.push('    ],');
    lines.push('  },');
  }

  lines.push('];');
  lines.push('');
  lines.push('export const router = createBrowserRouter(routes);');
  lines.push('');
  lines.push('export default router;');
  lines.push('');

  return lines.join('\n');
}

function buildRouteTree(routes: NavRouteEntry[]): Record<string, Record<string, NavRouteEntry[]>> {
  const tree: Record<string, Record<string, NavRouteEntry[]>> = {};

  for (const route of routes) {
    const parts = route.navRoute.split('.');
    const context = parts[0];
    const app = parts[1] || 'default';

    if (!tree[context]) {
      tree[context] = {};
    }
    if (!tree[context][app]) {
      tree[context][app] = [];
    }
    tree[context][app].push(route);
  }

  return tree;
}

function generateLayout(context: string): string {
  const contextCapitalized = capitalize(context);

  return `/**
 * ${contextCapitalized} Layout
 *
 * Auto-generated by SmartStack MCP - Customize as needed
 */

import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { ROUTES, getRoutesByContext } from '../routes/navRoutes.generated';

export const ${contextCapitalized}Layout: React.FC = () => {
  const location = useLocation();
  const contextRoutes = getRoutesByContext('${context}');

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <aside className="w-64 bg-white shadow-sm">
        <div className="p-4 border-b">
          <h1 className="text-xl font-semibold text-gray-900">${contextCapitalized}</h1>
        </div>
        <nav className="p-4">
          <ul className="space-y-2">
            {contextRoutes.map((route) => (
              <li key={route.navRoute}>
                <Link
                  to={route.web}
                  className={\`block px-3 py-2 rounded-md \${
                    location.pathname === route.web
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }\`}
                >
                  {route.navRoute.split('.').pop()}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default ${contextCapitalized}Layout;
`;
}

function generateRouteGuards(): string {
  return `/**
 * Route Guards
 *
 * Auto-generated by SmartStack MCP - Customize as needed
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

interface PermissionGuardProps {
  children: React.ReactNode;
  permissions: string[];
  fallback?: React.ReactNode;
}

/**
 * Protect routes that require authentication
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  redirectTo = '/login'
}) => {
  const location = useLocation();

  // TODO: Replace with your auth hook
  const isAuthenticated = true; // useAuth().isAuthenticated;

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

/**
 * Guard routes based on user permissions
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  permissions,
  fallback
}) => {
  // TODO: Replace with your auth hook
  const userPermissions: string[] = []; // useAuth().permissions;

  const hasPermission = permissions.length === 0 ||
    permissions.some(p => userPermissions.includes(p));

  if (!hasPermission) {
    if (fallback) return <>{fallback}</>;
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
          <p className="mt-2 text-gray-600">
            You don't have permission to access this page.
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Required: {permissions.join(', ')}
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
`;
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function formatResult(result: ScaffoldResult, input: ScaffoldRoutesInput): string {
  const lines: string[] = [];

  lines.push('# Scaffold Routes');
  lines.push('');

  if (input.options?.dryRun) {
    lines.push('> **DRY RUN** - No files were written');
    lines.push('');
  }

  lines.push('## Configuration');
  lines.push('');
  lines.push(`- **Source**: ${input.source}`);
  lines.push(`- **Scope**: ${input.scope}`);
  lines.push('');

  lines.push('## Generated Files');
  lines.push('');

  for (const file of result.files) {
    const relativePath = file.path.replace(/\\/g, '/').split('/src/').pop() || file.path;
    lines.push(`### ${relativePath}`);
    lines.push('');
    lines.push('```tsx');
    lines.push(file.content.substring(0, 2000) + (file.content.length > 2000 ? '\n// ... (truncated)' : ''));
    lines.push('```');
    lines.push('');
  }

  lines.push('## Instructions');
  lines.push('');
  for (const instruction of result.instructions) {
    lines.push(`- ${instruction}`);
  }

  return lines.join('\n');
}
